/**
 * AI Render — enum giá trị tham số diễn hoạ (design 10a §1).
 * NGUỒN SỰ THẬT dùng chung Viewer + Console: chỉ chứa MÃ enum + helper khóa i18n,
 * KHÔNG chứa chữ dịch (Viewer/Console tự khai báo bản dịch theo quy ước `aiOptLabelKey`).
 *
 * ⚠️ ĐỒNG BỘ BẮT BUỘC với backend: danh sách `value` ở đây = miền `params_json` mà
 * backend PromptComposer (design 03b) nhận + map sang prompt EN. Thêm/đổi value ở đây
 * PHẢI cập nhật bảng map EN tương ứng ở 03b, nếu không value mới bị bỏ qua (fallback im lặng).
 */

/** Loại diễn hoạ (mở rộng sau qua cả 2 đầu shared + 03d). */
export const RENDER_TYPES = ['architecture', 'landscape', 'interior'] as const;

/** Ánh sáng — ĐA CHỌN 0..N (10b). 'natural' chỉ 1 lần (yêu cầu gốc lặp — dedupe). */
export const LIGHTINGS = [
  'overcast',          // U ám
  'natural',           // Tự nhiên
  'sunny',             // Có nắng
  'interior-lights',   // Đèn Nội thất
  'evening-interior',  // Chiều tối (Nội thất)
  'dark-interior',     // Tối - Đen (Nội thất)
  'studio',            // Phòng thu
  'dappled-interior',  // Hoa Nắng (Nội thất)
  'golden-hour',       // Giờ vàng
  'indirect-led',      // LED Gián tiếp
  'other',             // Khác → nhập tự do (kèm RenderParams.lightingText)
] as const;

/** Góc nhìn — đi vào PROMPT (không đặt camera; xem ADR-AR2). 'keep' = giữ như ảnh gốc. Single. */
export const VIEW_ANGLES = [
  'keep',
  'front',
  'wide',
  'drone',
  'three-quarter-left',
  'three-quarter-right',
  'low',
  'high',
] as const;

/** Môi trường/bối cảnh — single. 'other' = Khác → kèm environmentText (free text). */
export const ENVIRONMENTS = [
  'urban',
  'street',
  'village',
  'countryside',
  'living-room',
  'bedroom',
  'kitchen',
  'other',
] as const;

/** Tông màu / color grade — ĐA CHỌN 0..N (10b). */
export const COLOR_TONES = [
  'natural',           // Tự nhiên
  'warm',              // Ấm áp
  'cool-modern',       // Lạnh hiện đại
  'bw',                // Đen Trắng
  'vintage-retro',     // Vintage/Retro
  'pastel',            // Pastel
  'cinematic',         // Điện ảnh
  'modern-warm',       // Hiện đại ấm áp
  'luxury',            // Luxury cao cấp
  'japandi',           // Japandi tối giản
  'premium-apartment', // Căn hộ cao cấp
  'kitchen-showroom',  // Showroom bếp
  'scandinavian',      // Scandinavian sáng nhẹ
] as const;

/** Vật liệu xuất hiện trong ảnh — ĐA CHỌN 0..N (10b). 'keep' = giữ nguyên như ảnh gốc. */
export const MATERIALS = [
  'keep',            // Giữ nguyên như ảnh gốc
  'wood',            // Gỗ
  'glass',           // Kính
  'stone',           // Đá
  'brick',           // Gạch
  'marble',          // Cẩm thạch
  'aluminum-glass',  // Nhôm kính
  'ceramic',         // Gốm sứ
  'bamboo-rattan',   // Tre / Mây
  'composite-panel', // Tấm ốp Composite
  'laterite',        // Đá ong
  'concrete',        // Bê tông
] as const;

/** Tỉ lệ ảnh đầu ra (giữ — backend OpenAI map size + Gemini hint; viewer dẫn xuất từ SIZE). */
export const ASPECTS = ['1:1', '4:3', '3:4', '16:9', '9:16'] as const;

/**
 * Kích thước ảnh phổ biến (10d) — id = "rộngxcao" px. longEdge ≤ 2048 để PNG capture không vượt
 * `maxBaseImageBytes` (8MB) ở backend. Thay bộ chọn "Tỉ lệ" trên UI; `aspect` được viewer dẫn xuất.
 */
export const SIZES = [
  '1024x1024',  // Vuông
  '1080x1080',  // Vuông (mạng xã hội)
  '1536x1024',  // Ngang (OpenAI landscape)
  '1024x1536',  // Dọc (OpenAI portrait)
  '1920x1080',  // Full HD ngang (16:9)
  '1080x1920',  // Full HD dọc (9:16)
  '1280x720',   // HD ngang (16:9)
  '2048x2048',  // Vuông lớn
] as const;

/**
 * "Tự tạo không gian phòng" (10e) — CHỈ dùng khi renderType==='interior'. Mỗi value PHẢI có cụm EN
 * tương ứng ở backend 03i (PromptComposer); thêm/đổi value ở đây phải đổi bảng map EN.
 */

/** Nguồn không gian phòng — toggle user tự khai (chưa auto-detect). */
export const ROOM_SOURCES = [
  'ai-generated',  // AI tạo phòng mới (thay nền trống/đen)
  'use-existing',  // Dùng tường/sàn/trần đã có trong model
] as const;

/** Tường — 'auto' = AI hài hòa với tủ. */
export const WALL_MODES = ['auto', 'light-warm', 'matte-white', 'soft-concrete', 'marble-accent', 'wood-accent'] as const;
/** Sàn. */
export const FLOOR_MODES = ['auto', 'light-tiles', 'warm-wood', 'light-stone', 'matte-concrete'] as const;
/** Trần + ánh sáng trần. */
export const CEILING_MODES = ['auto', 'recessed-spotlights', 'linear-led', 'pendant-island', 'minimal-flat'] as const;
/** Backsplash (ốp tường bếp). */
export const BACKSPLASH_MODES = ['auto', 'light-stone', 'white-tile', 'marble-slab', 'warm-gray', 'none'] as const;
/** Bàn đảo: none = không · one = có 1 (giữa phòng) · auto = AI tự quyết. */
export const ISLAND_MODES = ['none', 'one', 'auto'] as const;
/** Mức phụ kiện/decor. */
export const ACCESSORY_LEVELS = ['none', 'low', 'medium', 'high'] as const;

/**
 * "Bối cảnh ngoại cảnh" (10f) — CHỈ dùng khi renderType là 'architecture' / 'landscape'.
 * `'auto'` = AI tự quyết (backend bỏ cụm); `'none'` = vắng có chủ đích. Mỗi value PHẢI có cụm EN ở 03l.
 */

// --- Kiến trúc (renderType='architecture') ---
export const ARCH_SCENES = ['auto', 'urban-heritage-street', 'old-town', 'modern-city', 'tourist-street', 'garden-courtyard'] as const;
export const ARCH_SURROUNDINGS = ['auto', 'low-rise-nearby', 'high-rise-far', 'mixed', 'none'] as const;
export const ARCH_ACTIVITY = ['auto', 'none', 'light', 'medium', 'busy'] as const; // mật độ người+xe (nền)
export const ARCH_GROUNDS = ['auto', 'stone-pavement', 'sidewalk', 'asphalt'] as const;
export const SKY_MODES = ['auto', 'golden-hour', 'daylight', 'clear', 'overcast', 'night'] as const; // dùng chung arch+landscape

// --- Cảnh quan (renderType='landscape') ---
export const LAND_SCENES = ['auto', 'forest', 'streamside', 'mountain', 'japanese-garden', 'park', 'old-village', 'lakeside'] as const;
export const LAND_TERRAINS = ['auto', 'forest-clearing', 'gentle-hill', 'streamside', 'rocky', 'paved-path'] as const;
export const LAND_WATER = ['auto', 'none', 'stream-beside', 'stream-front', 'pond-front', 'waterfall-back'] as const;
export const LAND_HERO_TREES = ['auto', 'none', 'ancient-pine-behind', 'red-maple', 'bamboo', 'natural-forest'] as const;
export const LAND_GROUNDS = ['auto', 'stone-and-moss', 'grass', 'dirt'] as const;

export type RenderType = (typeof RENDER_TYPES)[number];
export type Lighting = (typeof LIGHTINGS)[number];
export type ViewAngle = (typeof VIEW_ANGLES)[number];
export type Environment = (typeof ENVIRONMENTS)[number];
export type ColorTone = (typeof COLOR_TONES)[number];
export type Material = (typeof MATERIALS)[number];
export type Aspect = (typeof ASPECTS)[number];
export type Size = (typeof SIZES)[number];
export type RoomSource = (typeof ROOM_SOURCES)[number];
export type WallMode = (typeof WALL_MODES)[number];
export type FloorMode = (typeof FLOOR_MODES)[number];
export type CeilingMode = (typeof CEILING_MODES)[number];
export type BacksplashMode = (typeof BACKSPLASH_MODES)[number];
export type IslandMode = (typeof ISLAND_MODES)[number];
export type AccessoryLevel = (typeof ACCESSORY_LEVELS)[number];
export type ArchScene = (typeof ARCH_SCENES)[number];
export type ArchSurrounding = (typeof ARCH_SURROUNDINGS)[number];
export type ArchActivity = (typeof ARCH_ACTIVITY)[number];
export type ArchGround = (typeof ARCH_GROUNDS)[number];
export type SkyMode = (typeof SKY_MODES)[number];
export type LandScene = (typeof LAND_SCENES)[number];
export type LandTerrain = (typeof LAND_TERRAINS)[number];
export type LandWater = (typeof LAND_WATER)[number];
export type LandHeroTree = (typeof LAND_HERO_TREES)[number];
export type LandGround = (typeof LAND_GROUNDS)[number];

/** Môi trường ngoại cảnh (architecture/landscape). ⊂ ENVIRONMENTS. */
export const OUTDOOR_ENVIRONMENTS = ['urban', 'street', 'village', 'countryside', 'other'] as const;
/** Phòng nội thất (interior) — dùng lại `environment` làm "loại phòng". ⊂ ENVIRONMENTS. */
export const INTERIOR_ENVIRONMENTS = ['living-room', 'bedroom', 'kitchen', 'other'] as const;

/** Danh sách environment hợp lệ theo renderType (viewer 04f đổ vào dropdown). */
export const environmentsFor = (renderType: string): readonly Environment[] =>
  renderType === 'interior' ? INTERIOR_ENVIRONMENTS : OUTDOOR_ENVIRONMENTS;

/** Tách "1920x1080" → {w,h} (px). Dùng ở viewer cho capture + dẫn xuất aspect. Lỗi → 1024². */
export const parseSize = (s: string): { w: number; h: number } => {
  const [w, h] = (s ?? '').split('x').map(Number);
  return { w: w > 0 ? w : 1024, h: h > 0 ? h : 1024 };
};

/**
 * Khóa i18n nhãn theo ƯỚC LỆ `airender.opt.<group>.<value>`
 * (vd 'airender.opt.lighting.sunset'). Viewer/Console khai báo bản dịch theo quy ước này.
 */
export const aiOptLabelKey = (group: string, value: string): string =>
  `airender.opt.${group}.${value}`;

/**
 * Bậc đánh giá model AI (10g) — Backend (03p) gắn qua `model-meta`; FE (04h/05g) render nhãn.
 * Trả MÃ code; bản dịch i18n do Viewer/Console khai báo theo `aiOptLabelKey('quality'|'price'|'speed', value)`.
 */
export const QUALITY_TIERS = ['very-high', 'high', 'fair'] as const;            // rất cao / cao / khá
export const PRICE_TIERS = ['very-expensive', 'expensive', 'medium', 'cheap'] as const; // rất đắt / đắt / trung bình / rẻ
export const SPEED_TIERS = ['fast', 'medium', 'slow'] as const;                // nhanh / trung bình / chậm

export type QualityTier = (typeof QUALITY_TIERS)[number];
export type PriceTier = (typeof PRICE_TIERS)[number];
export type SpeedTier = (typeof SPEED_TIERS)[number];

/**
 * Ghép nhãn hiển thị model: `"label (Chất lượng…, Giá…, Render…)"` hoặc `"label (mới)"`.
 * `t` = hàm dịch i18n của app (nhận key → chuỗi đã dịch). Model thiếu tier (chưa chấm) → badge "(mới)".
 * (Param dạng cấu trúc — KHÔNG import AiModelDto để tránh vòng phụ thuộc với types/api.ts.)
 */
export const formatAiModelLabel = (
  m: { label: string; qualityTier?: QualityTier | null; priceTier?: PriceTier | null; speedTier?: SpeedTier | null },
  t: (key: string) => string,
): string => {
  if (!m.qualityTier || !m.priceTier || !m.speedTier) {
    return `${m.label} (${t('airender.opt.modelTag.new')})`;
  }
  const q = t(aiOptLabelKey('quality', m.qualityTier));
  const p = t(aiOptLabelKey('price', m.priceTier));
  const s = t(aiOptLabelKey('speed', m.speedTier));
  return `${m.label} (${q}, ${p}, ${s})`;
};
